/**
 * Hooks form-responses (F2-Forms-1d-ii — remplissage conseiller → dossier).
 *
 * Consomme l'API privée broker 1b-ii (create/patch draft) + 1d-i (bascule
 * atomique `submit-to-intake`). Types dérivés des schemas shared via `z.infer`
 * (aucun re-typage). `api.{post,patch}` déballe déjà `.data` et lève
 * `ApiException(status, apiError)` sur !ok.
 *
 * Machine d'état réseau (pilotée par la page FormFillPage) :
 *   - create paresseux : `POST /v1/form-responses { template_version_id, data }`
 *     au 1er Save OU au Submit (jamais à l'ouverture) ;
 *   - `PATCH /v1/form-responses/:id { data }` seulement si dirty ;
 *   - `POST /v1/form-responses/:id/submit-to-intake { project_id }` = bascule
 *     atomique idempotente.
 *
 * L'`id` est passé au `mutate` (connu seulement après create) → les hooks ne
 * sont pas ré-instanciés quand le `responseId` apparaît. Le single-flight
 * (désactivation des boutons pendant la requête) est géré via `isPending` côté page.
 */
import type { Schemas } from "@kollectivia/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { z } from "zod";

import { api } from "../api";

export type FormResponseRow = z.infer<typeof Schemas.FormResponseSchema>;
export type FormResponseListItem = z.infer<typeof Schemas.FormResponseListItemSchema>;

/**
 * Filtres du registre des réponses (9.U.7) — partagés liste + export CSV. La
 * query string est la source de vérité (filtres partageables via l'URL de page).
 */
export type FormResponseListParams = {
  cursor?: string;
  status?: FormResponseRow["status"];
  submitted_from?: NonNullable<FormResponseRow["submitted_from"]>;
  template_id?: string;
  template_version_id?: string;
  project_id?: string;
  intake_link_id?: string;
  from?: string;
  to?: string;
};

export type FormResponseListResult = {
  items: FormResponseListItem[];
  next_cursor: string | null;
};

/** Construit la query string (filtres + cursor) — builder UNIQUE liste + CSV. */
function buildFormResponsesQuery(params: FormResponseListParams): string {
  const sp = new URLSearchParams();
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.status) sp.set("status", params.status);
  if (params.submitted_from) sp.set("submitted_from", params.submitted_from);
  if (params.template_id) sp.set("template_id", params.template_id);
  if (params.template_version_id) sp.set("template_version_id", params.template_version_id);
  if (params.project_id) sp.set("project_id", params.project_id);
  if (params.intake_link_id) sp.set("intake_link_id", params.intake_link_id);
  if (params.from) sp.set("from", params.from);
  if (params.to) sp.set("to", params.to);
  return sp.toString();
}

/** GET /v1/form-responses — registre keyset (métadonnées). Human-only côté API. */
export function useFormResponses(params: FormResponseListParams) {
  const qs = buildFormResponsesQuery(params);
  return useQuery({
    queryKey: ["form-responses", "list", qs],
    queryFn: () => api.get<FormResponseListResult>(`/v1/form-responses?${qs}`),
  });
}

/** Path CSV (mêmes filtres, sans cursor) — consommé via `apiDownload`. */
export function formResponsesCsvPath(params: FormResponseListParams): string {
  const { cursor: _omit, ...filters } = params;
  const qs = buildFormResponsesQuery(filters);
  return `/v1/form-responses/csv${qs ? `?${qs}` : ""}`;
}

/**
 * Retour de `GET /v1/form-responses/:id` (1b-ii) : réponse + template minimal +
 * version EXACTE liée (immuable) + son `schema`. Consommé en lecture seule par
 * le viewer 1e-A sur la fiche intake.
 */
export type FormResponseDetailResult = {
  mandate?: {
    id: string;
    artifact_file_id: string | null;
    signed_at: string;
    signed_by_name: string;
    expires_at: string;
    revoked_at: string | null;
  } | null;
  response: FormResponseRow;
  template: {
    id: string;
    code: string;
    name: string;
    status: string;
    default_language: "fr" | "en";
  };
  version: { id: string; version_number: number; schema: unknown };
};

/**
 * GET /v1/form-responses/:id — lecture seule (réponse + version exacte + schema).
 * Web-only : réutilise l'endpoint privé existant (aucune nouvelle API). `enabled`
 * seulement si `id` fourni. 404 (absent/cross-tenant/supprimé) → `error` géré côté
 * appelant en état neutre anti-leak.
 */
export function useFormResponse(id: string | undefined) {
  return useQuery({
    queryKey: ["form-responses", "detail", id],
    queryFn: () => api.get<FormResponseDetailResult>(`/v1/form-responses/${id}`),
    enabled: !!id,
  });
}

/** Retour de la bascule atomique 1d-i. `idempotent=true` = rejeu (aucun nouvel event). */
export type SubmitToIntakeResult = {
  response: FormResponseRow;
  intake_event_id: string;
  idempotent: boolean;
};

/**
 * POST /v1/form-responses — crée un brouillon lié à une version publiée.
 *
 * 9.BB.1 — `project_id` est optionnel et **proposé**, pas imposé : le serveur
 * vérifie que le dossier appartient au cabinet courant, refuse d'un seul 404
 * sinon, et rend le lien immuable ensuite. Sans lui, la réponse ne peut jamais
 * devenir le cahier de charges d'une demande, puisque l'attach exige l'égalité
 * des dossiers.
 */
export function useCreateFormResponse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      template_version_id: string;
      data: Record<string, unknown>;
      project_id?: string;
    }) => api.post<FormResponseRow>("/v1/form-responses", input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["form-responses"] });
    },
  });
}

/**
 * POST /v1/form-responses/:id/submit — draft → submitted, SANS intake.
 *
 * Le pendant de `useSubmitFormResponseToIntake` pour le parcours où le dossier
 * est déjà connu : il n'y a rien à résoudre, donc rien à matérialiser. Créer un
 * `intake_event` « à résoudre » pour un dossier qui existe déjà demanderait au
 * conseiller de trancher une question qu'il a déjà tranchée.
 */
export function useSubmitFormResponse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; expected_revision?: string }) =>
      api.post<FormResponseRow>(
        `/v1/form-responses/${input.id}/submit`,
        input.expected_revision ? { expected_revision: input.expected_revision } : {},
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["form-responses"], refetchType: "none" });
    },
  });
}

/** PATCH /v1/form-responses/:id — édite `data` d'un brouillon (mode draft). */
export function usePatchFormResponse() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      id: string;
      data: Record<string, unknown>;
      expected_revision?: string;
    }) =>
      api.patch<FormResponseRow>(`/v1/form-responses/${input.id}`, {
        data: input.data,
        ...(input.expected_revision ? { expected_revision: input.expected_revision } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["form-responses"] });
    },
  });
}

/**
 * POST /v1/form-responses/:id/submit-to-intake — bascule atomique draft→submitted
 * + création/lien de l'`intake_event`. Invalide `['intake-events']` (couvre
 * liste + détail + dashboard) au succès ; la navigation vers `/intake-events/:id`
 * est déclenchée par la page.
 */
export function useSubmitFormResponseToIntake() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; project_id: string; expected_revision?: string }) =>
      api.post<SubmitToIntakeResult>(`/v1/form-responses/${input.id}/submit-to-intake`, {
        project_id: input.project_id,
        ...(input.expected_revision ? { expected_revision: input.expected_revision } : {}),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["intake-events"] });
    },
  });
}

/** Existing list endpoint, scoped to the project; consume every page. */
export function useFormResponseDrafts(projectId: string) {
  return useQuery({
    queryKey: ["form-responses", "drafts", projectId],
    queryFn: async () => {
      const items: FormResponseListItem[] = [];
      let cursor: string | undefined;
      do {
        const page = await api.get<FormResponseListResult>(
          `/v1/form-responses?${buildFormResponsesQuery({ project_id: projectId, status: "draft", cursor })}`,
        );
        items.push(...page.items);
        cursor = page.next_cursor ?? undefined;
      } while (cursor);
      return items;
    },
  });
}
